import { useLayoutEffect, useRef, useState } from 'react';

const heightGroups = new Map<
  string,
  {
    max: number;
    listeners: Set<(height: number) => void>;
  }
>();

export const usePreservedHeight = (groupId?: string) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!groupId) return;
    const el = ref.current;
    if (!el) return;

    const applyMinHeight = (height: number) => {
      setMinHeight(prev => (prev === height ? prev : height));
    };

    const updateGroupHeight = (height: number) => {
      let group = heightGroups.get(groupId);
      if (!group) {
        group = { max: height, listeners: new Set() };
        heightGroups.set(groupId, group);
      }

      if (height > group.max) {
        group.max = height;
        group.listeners.forEach(listener => listener(group!.max));
      }
    };

    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (!rect.height) return;
      const normalized = Math.ceil(rect.height);
      if (!Number.isFinite(normalized) || normalized <= 0) return;
      updateGroupHeight(normalized);
    };

    let group = heightGroups.get(groupId);
    if (!group) {
      group = { max: 0, listeners: new Set() };
      heightGroups.set(groupId, group);
    }
    group.listeners.add(applyMinHeight);
    if (group.max > 0) {
      setTimeout(() => applyMinHeight(group!.max), 0);
    }

    measure();

    let frameId: number | null = null;
    let observer: ResizeObserver | null = null;

    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
        frameId = requestAnimationFrame(measure);
      });
      observer.observe(el);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      const existing = heightGroups.get(groupId);
      if (existing) {
        existing.listeners.delete(applyMinHeight);
      }
    };
  }, [groupId]);

  return { ref, minHeight };
};
